import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';

/**
 * Kaohsiung (高雄), the sunny port city of the south (kaohsiung package).
 *
 * North-west: THSR Zuoying station (高鐵左營站) with its wave roof, taxi loop and plaza — the way home.
 * North-east: Lotus Pond (蓮池潭) with the Dragon and Tiger Pagodas (龍虎塔; in through the dragon,
 *   out through the tiger), the Spring and Autumn Pavilions and the giant Xuantian statue.
 * Middle: Zhongshan Road runs south from the boulevard. At its crossing, Formosa Boulevard station
 *   (美麗島) with its glass entrances over the Dome of Light; east of it, Liuhe Night Market (六合夜市);
 *   west, Love River (愛河) with its promenade, boats and bridges, and the arcaded old streets of Yancheng.
 * South: Pier-2 Art Center (駁二) — brick warehouses, murals, the giant worker statues, the circular
 *   light rail — then the harbour quay, the Cijin ferry, kites and the 85 Sky Tower on the horizon.
 *
 * Ground chars of this package: '@' pavers (station granite, lotus-shore brick, Pier-2 concrete with old
 * rails, quay concrete), '$' LRT grass track (asphalt inside Liuhe), '%' building roofs (solid).
 */
const W = 96, H = 56;
const b = new MapBuilder(W, H, 'g');

// --- Edges -------------------------------------------------------------------------------------
b.fill(0, 0, W, 1, 'h').fill(0, 0, 1, 50, 'h').fill(95, 0, 1, 50, 'h');

// --- Zuoying HSR station: building, plaza, taxi loop ---------------------------------------------
b.fill(1, 1, 30, 15, '@');
b.fill(3, 2, 26, 6, '%');
b.fill(1, 12, 30, 2, 'r').fill(1, 14, 2, 2, 'r').fill(29, 14, 2, 2, 'r');
b.fill(14, 12, 3, 2, '=').fill(26, 12, 2, 2, '=');

// --- Apartment block with arcades and a small park between the station and the pond -------------
b.fill(32, 2, 15, 5, '%');
b.fill(31, 7, 17, 2, 's');
b.fill(31, 11, 17, 2, 'p').fill(38, 9, 2, 7, 'p');

// --- Lotus Pond: water, the shore walk, causeways to the pagodas and the pavilions ----------------
b.fill(48, 1, 47, 15, '@');
b.fill(49, 1, 46, 11, '~');
b.fill(70, 5, 2, 7, '@').fill(65, 6, 12, 1, '@');
b.fill(65, 3, 3, 3, '%').fill(74, 3, 3, 3, '%'); // dragon · tiger
b.fill(57, 11, 1, 1, '@').fill(56, 8, 3, 3, '%'); // Spring and Autumn Pavilions
b.fill(86, 7, 3, 3, '%'); // Xuantian statue base

// --- Boulevard (博愛路 / 中正路) across the city, Zhongshan Road south to the harbour ---------------
b.fill(0, 16, W, 3, 'r').fill(0, 17, W, 1, 'l');
for (const x of [14, 44, 69, 85]) b.fill(x, 16, 3, 3, '=');
b.fill(0, 19, W, 1, 's');
b.fill(43, 19, 5, 29, 's').fill(44, 19, 3, 29, 'r').fill(45, 19, 1, 29, 'l');
for (const y of [22, 31, 41]) b.fill(44, y, 3, 1, '=');
// Second east-west road (五福路).
b.fill(0, 33, W, 1, 's').fill(0, 37, W, 1, 's');
b.fill(0, 34, W, 3, 'r').fill(0, 35, W, 1, 'l');
for (const x of [14, 60, 85]) b.fill(x, 34, 3, 3, '=');
b.fill(44, 34, 3, 3, 'r').fill(45, 34, 1, 3, 'l');

// --- Love River: water from the boulevard to the harbour, promenades, bridges --------------------
b.fill(17, 19, 11, 31, '@');
b.fill(21, 19, 4, 31, 'q');
b.fill(21, 26, 4, 1, 'y').fill(21, 44, 4, 1, 'y').fill(21, 46, 4, 2, 'y');
b.fill(17, 33, 11, 1, 's').fill(17, 37, 11, 1, 's');
b.fill(21, 34, 4, 3, 'r').fill(21, 35, 4, 1, 'l');
// Riverside park on the east bank.
b.fill(28, 20, 10, 13, 'g');
b.fill(28, 24, 10, 1, 'p').fill(32, 20, 1, 13, 'p').fill(28, 30, 10, 1, 'p');

// --- Yancheng old quarter: two arcaded shophouse rows and the lane between -------------------------
b.fill(1, 19, 16, 14, 's');
b.fill(2, 20, 15, 4, '%').fill(2, 28, 15, 4, '%');

// --- Formosa Boulevard: plazas with the two glass entrances ---------------------------------------
b.fill(38, 19, 5, 9, '@').fill(48, 19, 5, 9, '@');
b.fill(38, 20, 4, 2, '%').fill(48, 20, 4, 2, '%');

// --- Liuhe Night Market: a pedestrian street between two shophouse rows ---------------------------
b.fill(53, 19, 25, 14, 's');
b.fill(54, 20, 23, 3, '%');
b.fill(53, 23, 25, 4, '$');
b.fill(54, 27, 23, 3, '%');

// --- East: apartment towers and a pocket park -----------------------------------------------------
b.fill(78, 19, 17, 14, 's');
b.fill(80, 20, 13, 5, '%');
b.fill(78, 25, 17, 8, 'g').fill(78, 28, 17, 1, 'p').fill(86, 25, 1, 8, 'p');

// --- Pier-2: warehouses, the plaza with the old rails, the LRT ------------------------------------
b.fill(1, 38, 42, 8, '@');
b.fill(2, 38, 8, 4, '%').fill(10, 38, 8, 4, '%').fill(28, 38, 8, 4, '%').fill(36, 38, 7, 4, '%');
b.fill(17, 38, 11, 8, '@');
b.fill(21, 38, 4, 6, 'q');
// Harbour park east of Zhongshan Road, with the 85 Sky Tower behind it.
b.fill(48, 38, 47, 8, 'g');
b.fill(48, 44, 47, 2, '@');
b.fill(60, 38, 2, 6, 'p').fill(76, 38, 2, 6, 'p');
b.fill(84, 38, 7, 5, '%');
// LRT grass track along the waterfront; the river passes under it on a bridge.
b.fill(1, 46, 94, 2, '$');
b.fill(21, 46, 4, 2, 'y');
// Quay, the harbour and the Cijin ferry pier.
b.fill(0, 48, W, 2, '@');
b.fill(21, 48, 4, 2, 'q');
b.fill(0, 50, W, 5, 'Z');
b.fill(10, 50, 4, 3, 'y');
b.fill(0, 55, W, 1, 'S');

// --- Decor ------------------------------------------------------------------------------------
const mid = (tx: number, ty: number, dx = 8, dy = 14) => ({ x: px(tx) + dx, y: px(ty) + dy });
const solidAt = (tx: number, ty: number, tw = 1, h = 8) => ({ x: px(tx) + 2, y: px(ty + 1) - h, w: px(tw) - 4, h });
const tree = (tx: number, ty: number, kind: 'tree' | 'pine' | 'cherry' = 'tree'): Decor => ({ kind, ...mid(tx, ty), solid: { x: px(tx) + 3, y: px(ty) + 8, w: 10, h: 6 } });
const palm = (tx: number, ty: number, v = 0): Decor => ({ kind: 'hc_palm', ...mid(tx, ty), w: 56, variant: v, solid: { x: px(tx) + 5, y: px(ty) + 8, w: 6, h: 6 } });
const banyan = (tx: number, ty: number): Decor => ({ kind: 'xy_banyan', ...mid(tx, ty), w: 80, solid: { x: px(tx) + 2, y: px(ty) + 6, w: 12, h: 8 } });
const lamp = (tx: number, ty: number): Decor => ({ kind: 'lamp', ...mid(tx, ty, 8, 12) });
const planter = (tx: number, ty: number): Decor => ({ kind: 'planter', ...mid(tx, ty, 8, 12), solid: { x: px(tx) - 6, y: px(ty) + 4, w: 28, h: 8 } });
const bench = (tx: number, ty: number, v = 0): Decor => ({ kind: 'kh_benchSit', ...mid(tx, ty, 8, 12), w: 30, variant: v, solid: { x: px(tx) - 7, y: px(ty) + 2, w: 30, h: 10 } });
const person = (tx: number, ty: number, v: number, dx = 8, dy = 12): Decor => ({ kind: 'xm_person', x: px(tx) + dx, y: px(ty) + dy, variant: v });
/** `kh_crowd`: count + 16·seed + 256·mode (see streetLife `crowd`). */
const crowd = (tx: number, ty: number, n: number, seed: number, mode = 0, w = 56, h = 24): Decor => ({ kind: 'kh_crowd', ...mid(tx, ty, 8, 12), w, h, variant: n + 16 * seed + 256 * mode });
const scooters = (tx: number, ty: number, w: number, v = 0): Decor => ({ kind: 'xy_scooterRow', x: px(tx) + px(w) / 2, y: px(ty) + 14, w: px(w), variant: v, solid: { x: px(tx) + 2, y: px(ty) + 4, w: px(w) - 4, h: 10 } });
const cables = (tx: number, ty: number, w = 8, v = 0): Decor => ({ kind: 'kh_cables', x: px(tx) + 8, y: px(ty) + 14, w: px(w), variant: v });
const vsign = (tx: number, ty: number, v: number): Decor => ({ kind: 'kh_vsign', x: px(tx) + 8, y: px(ty) + 14, variant: v });
const manhole = (tx: number, ty: number): Decor => ({ kind: 'kh_manhole', ...mid(tx, ty, 8, 8), w: 16 });
const puddle = (tx: number, ty: number, w = 26): Decor => ({ kind: 'kh_puddle', ...mid(tx, ty, 8, 8), w });
const pigeons = (tx: number, ty: number, v: number): Decor => ({ kind: 'xy_pigeons', ...mid(tx, ty, 8, 8), w: 40, variant: v });
const bollard = (tx: number, ty: number, v = 0): Decor => ({ kind: 'kh_bollard', ...mid(tx, ty, 8, 12), variant: v, solid: solidAt(tx, ty, 1, 6) });
const stall = (tx: number, ty: number, v: number): Decor => ({ kind: 'kh_stall', x: px(tx) + 24, y: px(ty) + 14, w: 48, variant: v, solid: { x: px(tx) + 2, y: px(ty) + 2, w: 44, h: 12 } });
const mural = (tx: number, ty: number, w: number, v: number): Decor => ({ kind: 'kh_mural', x: px(tx) + px(w) / 2, y: px(ty) + 14, w: px(w), variant: v });
const lotus = (tx: number, ty: number): Decor => ({ kind: 'ck_lotus', ...mid(tx, ty, 8, 10) });
const bike = (tx: number, ty: number, v = 0): Decor => ({ kind: 'xy_bicycle', ...mid(tx, ty, 8, 12), w: 20, variant: v });
const taxi = (tx: number, ty: number, left = false): Decor => ({ kind: 'xy_taxi', ...mid(tx, ty, 8, 12), w: 40, variant: left ? 1 : 0, solid: { x: px(tx) - 6, y: px(ty) + 2, w: 30, h: 10 } });
/** The 85 Sky Tower is 270 px tall: three 90 px slices stacked so the renderer never culls the top. */
const tower85 = (tx: number, ty: number): Decor[] => [0, 1, 2].map((k) => ({ kind: 'kh_tower85', x: px(tx) + 8, y: px(ty) + 16 - k * 90, w: 112, h: 90, variant: k }));

const decor: Decor[] = [
  // ---- Zuoying HSR station ----------------------------------------------------------------------
  { kind: 'kh_hsrStation', x: px(16), y: px(8), w: 416, h: 116 },
  ...[2, 29].map((tx) => palm(tx, 9, tx % 3)),
  ...[7, 24].map((tx) => palm(tx, 11, (tx + 1) % 3)),
  planter(10, 9), planter(21, 9), planter(5, 15), planter(27, 15),
  bench(9, 10, 0), bench(23, 10, 1), bench(13, 15, 2), bench(19, 15, 3),
  lamp(4, 8), lamp(28, 8), lamp(9, 14), lamp(22, 14),
  // Travellers with suitcases spilling out of the doors, a queue at the taxi rank, scooters, bikes.
  crowd(15, 9, 7, 3, 3, 72), crowd(21, 10, 4, 7, 3, 40), crowd(9, 11, 5, 2, 0, 56),
  { kind: 'kh_queue', ...mid(18, 11, 12, 12), variant: 5 + 16 * 2 + 256 * 1 },
  person(12, 11, 4 * 20 + 1), person(28, 15, 4 * 7 + 2),
  { kind: 'kh_stroller', ...mid(11, 10, 8, 12) },
  pigeons(14, 10, 2), pigeons(25, 9, 4),
  taxi(5, 13), taxi(10, 13), taxi(20, 13, true), taxi(24, 13, true),
  { kind: 'xy_bus', ...mid(16, 13, 8, 12), w: 72, solid: { x: px(14) + 2, y: px(13) - 6, w: 68, h: 12 } },
  scooters(1, 15, 4, 1), bike(9, 15, 0), bike(10, 15, 1), bike(11, 15, 2),
  { kind: 'kh_taxiSign', ...mid(3, 11, 8, 14) },
  { kind: 'kh_signpost', ...mid(17, 15, 8, 14), variant: 0 },
  { kind: 'trashBin', ...mid(7, 14, 8, 12) }, { kind: 'trashBin', ...mid(25, 14, 8, 12) },
  manhole(8, 11), puddle(19, 9, 30),
  { kind: 'xy_vending', ...mid(2, 8, 8, 14), solid: solidAt(2, 8, 1, 8) },
  { kind: 'xy_vending', ...mid(3, 8, 8, 14), solid: solidAt(3, 8, 1, 8) },
  { kind: 'trafficLight', ...mid(13, 15, 2, 14) }, { kind: 'trafficLight', ...mid(17, 15, 14, 14) },
  { kind: 'trafficLight', ...mid(43, 19, 2, 10) }, { kind: 'trafficLight', ...mid(47, 15, 14, 14) },
  { kind: 'trafficLight', ...mid(68, 15, 2, 14) },

  // ---- Apartments and the pocket park -----------------------------------------------------------
  { kind: 'kh_apartments', x: px(39) + 8, y: px(7), w: 240, h: 108, variant: 0 },
  ...[33, 41, 46].map((tx) => vsign(tx, 7, tx % 7)),
  scooters(32, 8, 5, 2), scooters(42, 8, 4, 0),
  cables(31, 8, 16, 0), cables(47, 8, 1, 0),
  { kind: 'kh_cat', ...mid(38, 8, 8, 12), variant: 1 },
  banyan(34, 13), banyan(44, 13),
  tree(32, 10), tree(36, 14, 'cherry'), tree(46, 10),
  bench(35, 11, 4), bench(42, 11, 5),
  { kind: 'xy_cafeTables', ...mid(41, 14, 8, 12), w: 40 },
  { kind: 'flowerbed', ...mid(33, 15, 8, 12) }, { kind: 'flowerbed', ...mid(44, 15, 8, 12) },
  pigeons(39, 13, 1), { kind: 'hc_dog', ...mid(36, 10, 8, 12), w: 20 },
  lamp(31, 10), lamp(47, 10), lamp(38, 15),

  // ---- Lotus Pond ----------------------------------------------------------------------------------
  { kind: 'kh_pagoda', x: px(66) + 8, y: px(6), w: 64, h: 124, variant: 0 },
  { kind: 'kh_pagoda', x: px(75) + 8, y: px(6), w: 64, h: 124, variant: 1 },
  { kind: 'kh_causeway', x: px(71), y: px(12), w: 32, h: 112 },
  { kind: 'kh_pavilion', x: px(57) + 8, y: px(11), w: 72, h: 96 },
  { kind: 'kh_xuantian', x: px(87) + 8, y: px(10), w: 80, h: 140 },
  ...[[51, 3], [54, 7], [60, 4], [79, 4], [82, 9], [91, 3], [52, 10]].map(([tx, ty]) => lotus(tx, ty)),
  { kind: 'kh_glints', x: px(60), y: px(11), w: 320, h: 120 },
  { kind: 'kh_glints', x: px(85), y: px(8), w: 200, h: 90, variant: 1 },
  { kind: 'hc_egret', ...mid(63, 11, 8, 10), w: 12 }, { kind: 'hc_egret', ...mid(84, 11, 8, 10), w: 12, variant: 1 },
  { kind: 'kh_railing', x: px(49), y: px(12) + 4, w: px(21) },
  { kind: 'kh_railing', x: px(72), y: px(12) + 4, w: px(23) },
  { kind: 'kh_templeGate', x: px(53) + 8, y: px(15) + 14, w: 72, solid: { x: px(52) + 6, y: px(15) + 6, w: 6, h: 6 } },
  ...[50, 61, 77, 92].map((tx) => palm(tx, 13, tx % 3)),
  banyan(55, 14), banyan(83, 14),
  bench(64, 14, 6), bench(80, 14, 7), bench(52, 14, 8),
  lamp(59, 13), lamp(74, 13), lamp(94, 13),
  crowd(70, 13, 6, 5, 2, 64), crowd(66, 6, 3, 1, 2, 40), person(72, 7, 4 * 15 + 1),
  { kind: 'kh_tripod', ...mid(78, 13, 8, 12), variant: 2 },
  { kind: 'kh_incense', ...mid(60, 12, 8, 14), solid: solidAt(60, 12, 1, 6) },
  { kind: 'kh_stall', x: px(89) + 8, y: px(14) + 14, w: 48, variant: 4, solid: { x: px(88) + 4, y: px(14) + 2, w: 40, h: 12 } },
  { kind: 'trashBin', ...mid(69, 14, 8, 12) }, bike(74, 14, 1), bike(75, 14, 2),
  { kind: 'kh_signpost', ...mid(49, 14, 8, 14), variant: 1 },

  // ---- Boulevard life ----------------------------------------------------------------------------
  ...[6, 56, 90].map((tx): Decor => ({ kind: 'kh_laneArrow', ...mid(tx, 16, 8, 12), variant: 1 })),
  ...[10, 74].map((tx): Decor => ({ kind: 'kh_laneArrow', ...mid(tx, 18, 8, 12), variant: 0 })),
  { kind: 'xy_bus', ...mid(31, 18, 8, 10), w: 72, variant: 1 },
  { kind: 'xy_scooterWave', ...mid(50, 18, 8, 10), w: 56 },
  { kind: 'xy_scooterWave', ...mid(77, 16, 8, 10), w: 56, variant: 1 },
  taxi(62, 16, true), { kind: 'parkedCar', x: px(20), y: px(16), variant: 1 }, { kind: 'parkedCar', x: px(88), y: px(18) - 2 },
  { kind: 'kh_walkers', ...mid(15, 17, 8, 10), w: 48, variant: 1 + 16 },
  { kind: 'kh_walkers', ...mid(70, 17, 8, 10), w: 48, variant: 3 + 16 },
  { kind: 'kh_bridgeRail', x: px(23), y: px(16), w: 64, variant: 0 },
  { kind: 'kh_bridgeRail', x: px(23), y: px(19) - 2, w: 64, variant: 1 },
  { kind: 'kh_bridgeRail', x: px(23), y: px(34), w: 64, variant: 0 },
  { kind: 'kh_bridgeRail', x: px(23), y: px(37) - 2, w: 64, variant: 1 },
  { kind: 'kh_streetSign', ...mid(43, 19, 4, 14), variant: 0 },
  { kind: 'kh_streetSign', ...mid(47, 33, 12, 14), variant: 1 },
  { kind: 'kh_streetSign', ...mid(27, 33, 12, 14), variant: 2 },
  ...[2, 52, 92].map((tx): Decor => ({ kind: 'kh_laneArrow', ...mid(tx, 34, 8, 12), variant: 1 })),
  ...[8, 66].map((tx): Decor => ({ kind: 'kh_laneArrow', ...mid(tx, 36, 8, 12), variant: 0 })),
  { kind: 'xy_bus', ...mid(72, 36, 8, 10), w: 72 },
  taxi(5, 34), taxi(50, 36, true), { kind: 'parkedCar', x: px(30), y: px(34) - 2, variant: 1 },
  { kind: 'xy_scooterWave', ...mid(88, 36, 8, 10), w: 56 },
  { kind: 'kh_walkers', ...mid(15, 35, 8, 10), w: 48, variant: 2 + 16 },
  { kind: 'kh_walkers', ...mid(61, 35, 8, 10), w: 48, variant: 4 + 16 },
  // Zhongshan Road.
  ...[26, 39].map((ty): Decor => ({ kind: 'kh_laneArrow', ...mid(44, ty, 8, 12), variant: 2 })),
  ...[24, 46].map((ty): Decor => ({ kind: 'kh_laneArrow', ...mid(46, ty, 8, 12), variant: 3 })),
  { kind: 'xy_scooterWave', ...mid(44, 30, 8, 10), w: 40, variant: 2 },
  { kind: 'kh_walkers', ...mid(45, 22, 8, 10), w: 48, variant: 5 },
  { kind: 'kh_walkers', ...mid(45, 41, 8, 10), w: 48, variant: 6 },
  ...[21, 39].map((ty) => tree(43, ty)), ...[30].map((ty) => tree(47, ty)),
  lamp(43, 24), lamp(47, 39),

  // ---- Yancheng old quarter: arcaded shophouses ------------------------------------------------
  { kind: 'kh_shophouse', x: px(9) + 8, y: px(24), w: 240, h: 100, variant: 0 },
  { kind: 'kh_shophouse', x: px(9) + 8, y: px(32), w: 240, h: 100, variant: 1 },
  ...[3, 9, 15].map((tx) => vsign(tx, 24, tx % 7)),
  ...[5, 13].map((tx) => vsign(tx, 32, (tx + 3) % 7)),
  scooters(2, 25, 5, 3), scooters(10, 25, 4, 1), scooters(5, 33, 6, 2),
  cables(1, 26, 16, 0), cables(17, 26, 1, 0),
  { kind: 'kh_tinShed', ...mid(4, 19, 8, 14), w: 48, variant: 1 }, { kind: 'kh_tinShed', ...mid(13, 19, 8, 14), w: 40, variant: 0 },
  { kind: 'kh_washing', x: px(6) + 8, y: px(27), w: 20 }, { kind: 'kh_washing', x: px(15), y: px(27), w: 16 },
  { kind: 'kh_cat', ...mid(16, 27, 8, 12), variant: 8 },
  { kind: 'hc_dog', ...mid(8, 26, 8, 12), w: 20, variant: 1 },
  { kind: 'kh_foodTruck', ...mid(14, 27, 8, 12), variant: 1, solid: { x: px(13), y: px(27) + 2, w: 44, h: 10 } },
  { kind: 'kh_tea', ...mid(3, 27, 8, 14), solid: { x: px(2) + 4, y: px(27) + 4, w: 24, h: 10 } },
  person(6, 25, 4 * 3 + 2), person(11, 27, 4 * 21), person(15, 24, 4 * 17 + 3),
  { kind: 'kh_person', ...mid(9, 27, 8, 12), variant: 5 * 128 + 4 * 2 },
  manhole(12, 26), puddle(5, 24, 24), puddle(10, 32, 28), { kind: 'trashBin', ...mid(16, 25, 8, 12) },
  { kind: 'kh_temple', x: px(9) + 8, y: px(27) + 14, w: 60, variant: 0 },
  lamp(1, 23), lamp(1, 31),

  // ---- Love River -------------------------------------------------------------------------------
  ...[27, 35, 43, 49].map((ty): Decor => ({ kind: 'kh_riverGlow', x: px(23), y: px(ty), w: 64, h: ty === 49 ? 96 : 128 })),
  ...[36, 44].flatMap((ty): Decor[] => [
    { kind: 'kh_loveBoat', x: px(23), y: px(ty), w: 48, h: 128 },
    { kind: 'kh_loveBoat', x: px(23), y: px(ty), w: 48, h: 128, variant: 1 },
  ]),
  { kind: 'kh_pedalBoat', x: px(22), y: px(31), w: 24 },
  { kind: 'kh_pedalBoat', x: px(24), y: px(37), w: 24, variant: 1 },
  { kind: 'kh_dock', x: px(25), y: px(29) + 14, w: 32 },
  { kind: 'kh_railing', x: px(18), y: px(19) + 4, w: px(3), variant: 1 },
  ...[21, 28, 39, 45].flatMap((ty): Decor[] => [
    { kind: 'kh_heartRail', x: px(20) + 8, y: px(ty) + 14, variant: ty % 3 },
    { kind: 'kh_heartRail', x: px(25) + 8, y: px(ty) + 14, variant: (ty + 1) % 3, w: -1 },
  ]),
  ...[20, 30, 39].map((ty) => palm(18, ty, ty % 3)),
  ...[21, 32, 45].map((ty) => palm(27, ty, (ty + 1) % 3)),
  ...[23, 42].map((ty) => tree(19, ty, 'cherry')), ...[31, 44].map((ty) => tree(26, ty)),
  bench(19, 26, 9), bench(26, 25, 10), bench(19, 41, 11), bench(26, 39, 12),
  lamp(18, 22), lamp(27, 29), lamp(18, 41), lamp(27, 46),
  { kind: 'xy_cafeTables', ...mid(26, 21, 8, 12), w: 40 },
  { kind: 'kh_cafe', x: px(26) + 8, y: px(19) + 14, w: 40 },
  ...[27, 34, 41, 47].flatMap((ty): Decor[] => [
    { kind: 'kh_cyclist', x: px(19) + 8, y: px(ty), h: px(7) },
    { kind: 'kh_cyclist', x: px(26) + 4, y: px(ty), h: px(7), variant: 1 },
    { kind: 'kh_jogger', x: px(27) + 2, y: px(ty), h: px(7) },
  ]),
  person(20, 24, 4 * 13), person(25, 32, 4 * 6 + 3), person(19, 39, 4 * 18 + 1), person(26, 43, 4 * 31 + 2),
  { kind: 'kh_person', ...mid(20, 31, 8, 12), variant: 8 * 128 + 4 * 2 + 1 },
  { kind: 'hc_egret', ...mid(20, 47, 8, 10), w: 12, variant: 1 },
  // Riverside park on the east bank.
  { kind: 'kh_parterre', ...mid(30, 22, 8, 12), w: 44, h: 22, variant: 0 },
  { kind: 'kh_parterre', ...mid(35, 28, 8, 12), w: 44, h: 22, variant: 2 },
  banyan(29, 27), banyan(36, 22), tree(30, 32, 'cherry'), tree(35, 32), tree(37, 26),
  bench(33, 26, 13), bench(29, 31, 14),
  { kind: 'kh_taichi', ...mid(31, 29, 8, 12), w: 48 },
  { kind: 'kh_ubikeDock', ...mid(29, 20, 8, 12), w: 64, solid: { x: px(28) + 2, y: px(20) + 4, w: 60, h: 8 } },
  { kind: 'kh_sculpture', ...mid(34, 31, 8, 14), variant: 0, solid: solidAt(34, 31, 1, 8) },
  pigeons(33, 23, 3), person(33, 21, 4 * 2 + 1), { kind: 'hc_dog', ...mid(32, 27, 8, 12), w: 20 },
  lamp(28, 23), lamp(37, 30),

  // ---- Formosa Boulevard --------------------------------------------------------------------------
  { kind: 'kh_glassEntrance', x: px(40), y: px(22), w: 92, h: 92, variant: 0 },
  { kind: 'kh_glassEntrance', x: px(50), y: px(22), w: 92, h: 92, variant: 1 },
  { kind: 'kh_domeSign', ...mid(42, 24, 8, 14), solid: solidAt(42, 24, 1, 6) },
  crowd(40, 25, 5, 4, 2, 56), crowd(50, 25, 4, 9, 1, 48),
  { kind: 'kh_queue', ...mid(49, 24, 4, 12), variant: 3 + 16 * 4 + 256 * 2 },
  { kind: 'kh_tripod', ...mid(38, 26, 8, 12), variant: 1 },
  { kind: 'kh_person', ...mid(42, 26, 8, 12), variant: 4 * 128 + 4 * 9 + 1 },
  planter(38, 27), planter(42, 27), planter(48, 27), planter(52, 27),
  ...[38, 52].map((tx) => palm(tx, 20, tx % 3)), ...[42, 48].map((tx) => palm(tx, 24, (tx + 2) % 3)),
  bench(39, 24, 15), bench(51, 24, 16),
  { kind: 'kh_busStop', ...mid(39, 19, 8, 14), w: 48 }, { kind: 'kh_busStop', ...mid(49, 19, 8, 14), w: 48, variant: 1 },
  { kind: 'kh_ubikeDock', x: px(50) + 8, y: px(27) + 14, w: 48 },
  pigeons(41, 26, 5), manhole(48, 23), puddle(40, 23, 28),
  { kind: 'trashBin', ...mid(43, 26, 8, 12) }, { kind: 'trashBin', ...mid(47, 26, 8, 12) },
  lamp(38, 22), lamp(52, 22), lamp(38, 26), lamp(52, 26),
  // Between the park and the plaza, the bank's ATM lobby and a convenience store front.
  { kind: 'kh_store', x: px(40) + 8, y: px(32) + 14, w: 96, solid: { x: px(38) + 8, y: px(32) - 10, w: 72, h: 20 } },
  { kind: 'kh_store', x: px(50) + 8, y: px(32) + 14, w: 96, variant: 1, solid: { x: px(48) + 8, y: px(32) - 10, w: 72, h: 20 } },
  scooters(38, 31, 3, 0), scooters(48, 31, 3, 2),

  // ---- Liuhe Night Market -----------------------------------------------------------------------
  { kind: 'kh_shophouse', x: px(65) + 8, y: px(23), w: 368, h: 100, variant: 2 },
  { kind: 'kh_shophouse', x: px(65) + 8, y: px(30), w: 368, h: 100, variant: 3 },
  { kind: 'kh_liuheGate', x: px(53) + 8, y: px(26) + 14, w: 72, h: 80 },
  ...[59, 65, 71, 75].map((tx, i) => stall(tx, 26, i)),
  { kind: 'kh_stall', x: px(74) + 8, y: px(23) + 14, w: 48, variant: 5, solid: { x: px(73) + 4, y: px(23) + 2, w: 40, h: 12 } },
  ...[57, 63, 69].map((tx, i): Decor => ({ kind: 'kh_tables', x: px(tx) + 8, y: px(25) + 12, w: 40, variant: i, solid: { x: px(tx) - 6, y: px(25) + 2, w: 28, h: 10 } })),
  ...[55, 63, 71].map((tx, i): Decor => ({ kind: 'lanternString', x: px(tx), y: px(23) + 8, w: 96, variant: i % 4 })),
  ...[54, 62, 70].map((tx, i): Decor => ({ kind: 'hc_bulbs', x: px(tx) + 48, y: px(26) + 8, w: 96, variant: i })),
  ...[57, 64, 71].map((tx) => vsign(tx, 23, (tx + 1) % 7)),
  ...[58, 64, 70].map((tx, i): Decor => ({ kind: 'hc_neon', ...mid(tx, 30, 8, 12), w: 16, variant: i })),
  ...[55, 62, 69, 76].map((tx, i): Decor => ({ kind: 'hc_crowd', ...mid(tx, 24, 8, 12), w: 60, variant: i })),
  ...[59, 66, 73].map((tx, i): Decor => ({ kind: 'hc_crowd', ...mid(tx, 25, 8, 14), w: 60, variant: i + 2 })),
  person(66, 25, 4 * 12 + 3), person(54, 25, 4 * 8 + 1),
  { kind: 'kh_stools', ...mid(56, 26, 8, 12), w: 40 }, { kind: 'kh_stools', ...mid(62, 26, 8, 12), w: 40, variant: 1 }, { kind: 'kh_stools', ...mid(68, 26, 8, 12), w: 40, variant: 2 },
  scooters(54, 30, 6, 1), scooters(68, 30, 6, 3), scooters(54, 19, 5, 0), scooters(70, 19, 6, 2),
  cables(53, 31, 16, 0), cables(69, 31, 8, 1), cables(53, 19, 16, 1), cables(69, 19, 8, 0),
  { kind: 'kh_tinShed', ...mid(58, 19, 8, 14), w: 44, variant: 2 }, { kind: 'kh_tinShed', ...mid(72, 27, 8, 14), w: 40, variant: 0 },
  { kind: 'kh_washing', x: px(63), y: px(23), w: 18 }, { kind: 'kh_washing', x: px(69) + 4, y: px(30), w: 18 },
  { kind: 'kh_cat', ...mid(77, 24, 8, 12), variant: 2 },
  { kind: 'hc_dog', ...mid(60, 31, 8, 12), w: 20 },
  manhole(65, 24), puddle(60, 25, 24), { kind: 'trashBin', ...mid(53, 24, 8, 12) },
  lamp(53, 22), lamp(77, 22), lamp(53, 27), lamp(77, 27),

  // ---- East towers and the pocket park --------------------------------------------------------------
  { kind: 'kh_towers', x: px(86) + 8, y: px(25), w: 208, h: 150 },
  scooters(79, 19, 6, 2), cables(78, 19, 16, 0), cables(94, 19, 1, 0),
  banyan(80, 30), banyan(92, 30), tree(84, 26), tree(90, 26, 'cherry'), tree(79, 32),
  { kind: 'kh_pavilionSmall', ...mid(83, 31, 8, 14), w: 60, solid: { x: px(82) + 2, y: px(31) - 4, w: 28, h: 10 } },
  bench(82, 27, 17), bench(90, 32, 18), { kind: 'kh_taichi', ...mid(89, 30, 8, 12), w: 48, variant: 1 },
  { kind: 'kh_exercise', ...mid(93, 27, 8, 12), solid: solidAt(93, 27, 1, 6) },
  { kind: 'kh_parterre', ...mid(88, 28, 8, 12), w: 44, h: 22, variant: 1 },
  pigeons(84, 29, 6), person(91, 29, 4 * 19 + 2), { kind: 'kh_stroller', ...mid(80, 28, 8, 12), variant: 1 },
  lamp(79, 27), lamp(94, 27), lamp(86, 32),
  { kind: 'kh_temple', x: px(93) + 4, y: px(32) + 14, w: 60, variant: 1 },

  // ---- Pier-2 Art Center ----------------------------------------------------------------------------
  { kind: 'hc_warehouse', x: px(6), y: px(42), w: 128, h: 88 },
  { kind: 'hc_warehouse2', x: px(14), y: px(42), w: 128, h: 88 },
  { kind: 'hc_warehouse', x: px(32), y: px(42), w: 128, h: 88, variant: 1 },
  { kind: 'hc_warehouse3', x: px(39) + 8, y: px(42), w: 112, h: 88 },
  mural(3, 42, 6, 0), mural(29, 42, 6, 1), mural(37, 42, 5, 2),
  { kind: 'kh_containers', x: px(20) + 8, y: px(42), w: 80, variant: 0, solid: { x: px(18) + 4, y: px(40), w: 72, h: 20 } },
  { kind: 'kh_containers', x: px(2) + 8, y: px(46), w: 60, variant: 1, solid: { x: px(1) + 4, y: px(44) + 2, w: 44, h: 10 } },
  { kind: 'kh_sculpture', ...mid(8, 44, 8, 14), variant: 1, solid: solidAt(8, 44, 1, 8) },
  { kind: 'kh_sculpture', ...mid(25, 44, 8, 14), variant: 2, solid: solidAt(25, 44, 1, 8) },
  { kind: 'kh_sculpture', ...mid(40, 44, 8, 14), variant: 3, solid: solidAt(40, 44, 1, 8) },
  { kind: 'kh_artist', ...mid(35, 43, 8, 12), w: 30 },
  { kind: 'kh_graffitiWall', x: px(18), y: px(42), w: 32, variant: 0 },
  { kind: 'kh_lrtStop', x: px(39) + 8, y: px(45) + 14, w: 72 },
  { kind: 'kh_lrtStop', x: px(5) + 8, y: px(45) + 14, w: 72, variant: 1 },
  ...[9, 25, 41, 57, 73, 89].map((tx): Decor => ({ kind: 'kh_tram', x: px(tx), y: px(47) + 8, w: 256 })),
  { kind: 'kh_trackPosts', x: px(10), y: px(47) + 12, w: px(20) },
  { kind: 'kh_trackPosts', x: px(80), y: px(47) + 12, w: px(14) },
  crowd(12, 44, 5, 6, 2, 60), crowd(34, 45, 4, 8, 2, 48), person(4, 43, 4 * 10 + 3), person(27, 43, 4 * 23 + 1),
  { kind: 'kh_tripod', ...mid(30, 42, 8, 12), variant: 0 },
  { kind: 'kh_skater', ...mid(16, 44, 8, 12), w: 40 },
  bike(2, 43, 1), bike(3, 43, 2), { kind: 'kh_ubikeDock', ...mid(27, 46, 8, 10), w: 64, solid: { x: px(26) + 2, y: px(46) + 2, w: 60, h: 8 } },
  bench(11, 45, 19), bench(36, 45, 20),
  { kind: 'kh_foodTruck', ...mid(42, 45, 8, 12), variant: 0, solid: { x: px(41) + 2, y: px(45) + 2, w: 44, h: 10 } },
  planter(1, 44), planter(12, 43), palm(1, 42, 2), palm(17, 46, 0), palm(26, 46, 1), palm(42, 43, 2),
  { kind: 'hc_dog', ...mid(6, 45, 8, 12), w: 20 }, pigeons(22, 45, 7),
  manhole(29, 45), puddle(14, 42, 30), puddle(38, 44, 26), { kind: 'kh_leaves', ...mid(20, 44, 8, 8), w: 40 },
  lamp(9, 42), lamp(24, 42), lamp(16, 46), lamp(43, 46),
  { kind: 'kh_signpost', ...mid(28, 42, 8, 14), variant: 2 },

  // ---- Harbour park, 85 Sky Tower, the quay and the Cijin ferry ---------------------------------------
  ...tower85(87, 42),
  { kind: 'kh_lowRise', x: px(79) + 8, y: px(43), w: 80, h: 70, variant: 0 },
  { kind: 'kh_lowRise', x: px(93) + 8, y: px(43), w: 48, h: 60, variant: 1 },
  { kind: 'ck_kites', ...mid(56, 42, 8, 12) },
  { kind: 'ck_kites', ...mid(70, 44, 8, 12), variant: 1 },
  { kind: 'kh_kites', x: px(63), y: px(40), w: 160, h: 120 },
  ...[50, 66, 82].map((tx) => palm(tx, 40, tx % 3)),
  ...[52, 72].map((tx) => palm(tx, 45, (tx + 1) % 3)),
  banyan(54, 43), banyan(80, 41), tree(66, 43), tree(49, 39, 'cherry'),
  bench(63, 42, 21), bench(51, 45, 23),
  { kind: 'kh_parterre', ...mid(69, 40, 8, 12), w: 44, h: 22, variant: 3 },
  { kind: 'kh_picnic', ...mid(74, 42, 8, 12), w: 40 },
  { kind: 'hc_dog', ...mid(60, 45, 8, 12), w: 20, variant: 1 }, pigeons(58, 44, 8),
  { kind: 'kh_stroller', ...mid(65, 45, 8, 12) },
  lamp(49, 45), lamp(77, 45),
  // The quay.
  { kind: 'kh_surf', x: px(48), y: px(50), w: px(96), h: 16 },
  { kind: 'kh_pierKh', x: px(12), y: px(53), w: 64, h: 48 },
  { kind: 'kh_ferry', x: px(17) + 8, y: px(52) + 8, w: 96 },
  { kind: 'kh_fishBoat', x: px(40), y: px(53), w: 60 },
  { kind: 'kh_fishBoat', x: px(66), y: px(52) + 8, w: 60, variant: 1 },
  { kind: 'kh_fishBoat', x: px(82), y: px(54), w: 60, variant: 2 },
  { kind: 'kh_cargoShip', x: px(55), y: px(54) + 12, w: 180 },
  { kind: 'kh_cijin', x: px(48), y: px(56), w: px(96), h: 40 },
  ...[2, 20, 35, 50, 66, 86, 92].map((tx) => bollard(tx, 49, tx % 2)),
  { kind: 'kh_lifebuoy', ...mid(14, 48, 8, 14) }, { kind: 'kh_lifebuoy', ...mid(45, 48, 8, 14), variant: 1 },
  { kind: 'kh_fisherman', ...mid(30, 49, 8, 14) }, { kind: 'kh_fisherman', ...mid(70, 49, 8, 14), variant: 1 },
  { kind: 'kh_surfboards', ...mid(8, 48, 8, 14), solid: { x: px(7) + 4, y: px(48) + 4, w: 24, h: 10 } },
  { kind: 'kh_ferrySign', ...mid(9, 49, 8, 14), solid: solidAt(9, 49, 1, 6) },
  crowd(12, 49, 5, 11, 3, 40, 12), crowd(33, 48, 4, 12, 2, 56, 14), crowd(62, 48, 3, 13, 0, 40, 14),
  { kind: 'kh_queue', ...mid(10, 49, 12, 12), variant: 4 + 16 * 6 + 256 * 1 },
  { kind: 'kh_person', ...mid(24, 48, 8, 12), variant: 8 * 128 + 4 * 30 + 1 }, person(52, 49, 4 * 16 + 1),
  { kind: 'kh_gull', x: px(20), y: px(48), w: 200, h: 60 },
  { kind: 'kh_gull', x: px(70), y: px(47), w: 240, h: 70, variant: 1 },
  bench(27, 48, 24), bench(79, 48, 26),
  palm(5, 48, 1), palm(48, 48, 0), palm(76, 48, 2),
  { kind: 'trashBin', ...mid(13, 48, 8, 12) }, { kind: 'trashBin', ...mid(60, 48, 8, 12) },
  lamp(3, 48), lamp(32, 48), lamp(57, 48), lamp(83, 48),
  { kind: 'kh_signpost', ...mid(44, 48, 8, 14), variant: 3 },
];

// --- Night lights -------------------------------------------------------------------------------
const warm = '255,214,150', cool = '200,230,255', pink = '255,150,200', violet = '170,140,255', neon = '255,120,80', gold = '255,190,90';
const lights: Light[] = [
  // Station: a bright canopy, plaza lamps, taxi rank.
  ...[6, 12, 18, 24].map((tx) => ({ x: px(tx) + 8, y: px(7), r: 90, color: cool })),
  { x: px(16), y: px(7), r: 60, color: '255,120,120' },
  ...[4, 12, 20, 28].map((tx) => ({ x: px(tx) + 8, y: px(8) - 14, r: 70, color: warm })),
  { x: px(9) + 8, y: px(14), r: 60, color: warm }, { x: px(22) + 8, y: px(14), r: 60, color: warm },
  { x: px(25), y: px(10), r: 50, color: '255,90,90' },
  // Park and apartments.
  { x: px(31) + 8, y: px(10), r: 60, color: warm }, { x: px(47) + 8, y: px(10), r: 60, color: warm }, { x: px(38) + 8, y: px(15), r: 60, color: warm },
  ...[33, 41, 46].map((tx) => ({ x: px(tx) + 8, y: px(6), r: 40, color: neon })),
  // Lotus Pond: the pagodas, the pavilion and the statue glow gold on the water.
  { x: px(66) + 8, y: px(3), r: 110, color: gold }, { x: px(75) + 8, y: px(3), r: 110, color: gold },
  { x: px(57) + 8, y: px(8), r: 80, color: gold }, { x: px(87) + 8, y: px(5), r: 90, color: '200,210,255' },
  ...[59, 68, 74, 88, 94].map((tx) => ({ x: px(tx) + 8, y: px(13) - 14, r: 60, color: warm })),
  { x: px(89) + 8, y: px(13), r: 50, color: gold },
  // Boulevards.
  ...[14, 45, 70, 86].map((tx) => ({ x: px(tx) + 8, y: px(17) + 8, r: 70, color: cool })),
  ...[14, 61, 86].map((tx) => ({ x: px(tx) + 8, y: px(35) + 8, r: 70, color: cool })),
  ...[24, 30, 44].map((ty) => ({ x: px(45) + 8, y: px(ty), r: 60, color: cool })),
  // Yancheng neon and lamps.
  ...[3, 7, 12, 16].map((tx) => ({ x: px(tx) + 8, y: px(23), r: 44, color: neon })),
  ...[4, 9, 14].map((tx) => ({ x: px(tx) + 8, y: px(31), r: 44, color: neon })),
  { x: px(1) + 8, y: px(22), r: 60, color: warm }, { x: px(1) + 8, y: px(30), r: 60, color: warm },
  { x: px(9) + 8, y: px(27), r: 40, color: '255,120,60' },
  // Love River: pink and violet lights reflected on the water.
  ...[22, 28, 41, 46].flatMap((ty) => [
    { x: px(18) + 8, y: px(ty), r: 70, color: pink },
    { x: px(27) + 8, y: px(ty) + 8, r: 70, color: violet },
  ]),
  { x: px(23), y: px(26), r: 60, color: pink }, { x: px(23), y: px(44), r: 60, color: violet },
  { x: px(26), y: px(29), r: 50, color: warm }, { x: px(26) + 8, y: px(19), r: 50, color: warm },
  { x: px(28) + 8, y: px(23), r: 60, color: warm }, { x: px(37) + 8, y: px(30), r: 60, color: warm },
  // Formosa Boulevard: the Dome of Light colours spill out of the glass shells.
  { x: px(40), y: px(21), r: 90, color: '255,120,200' }, { x: px(50), y: px(21), r: 90, color: '120,190,255' },
  { x: px(40), y: px(21), r: 50, color: '120,255,200' }, { x: px(50), y: px(21), r: 50, color: '255,220,100' },
  ...[[38, 22], [52, 22], [38, 26], [52, 26]].map(([tx, ty]) => ({ x: px(tx) + 8, y: px(ty), r: 60, color: warm })),
  { x: px(40) + 8, y: px(32), r: 70, color: '220,255,220' }, { x: px(50) + 8, y: px(32), r: 70, color: '255,235,200' },
  // Liuhe: strings of bulbs and lanterns, neon, stall lamps.
  ...[55, 61, 67, 73].map((tx) => ({ x: px(tx) + 48, y: px(23) + 8, r: 80, color: '255,170,110' })),
  ...[54, 60, 66, 72].map((tx) => ({ x: px(tx) + 48, y: px(26) + 8, r: 80, color: warm })),
  ...[59, 65, 71, 75].map((tx) => ({ x: px(tx) + 24, y: px(26), r: 50, color: gold })),
  ...[56, 62, 68, 74].map((tx) => ({ x: px(tx) + 24, y: px(23), r: 50, color: gold })),
  ...[58, 64, 70].map((tx) => ({ x: px(tx) + 8, y: px(30), r: 44, color: neon })),
  { x: px(53) + 8, y: px(25), r: 70, color: '255,100,100' },
  // East towers and the park.
  ...[79, 94, 86].map((tx) => ({ x: px(tx) + 8, y: px(27) + 8, r: 60, color: warm })),
  { x: px(93) + 4, y: px(32), r: 40, color: '255,120,60' },
  // Pier-2: warehouse lamps and the LRT stops; the harbour quay.
  ...[9, 24, 1, 16, 33, 43].map((tx) => ({ x: px(tx) + 8, y: px(tx < 20 && tx !== 1 && tx !== 16 ? 42 : 46) - 14, r: 60, color: warm })),
  { x: px(39) + 8, y: px(45), r: 60, color: cool }, { x: px(5) + 8, y: px(45), r: 60, color: cool },
  { x: px(20) + 8, y: px(42), r: 50, color: '120,255,220' },
  ...[3, 19, 32, 42, 57, 68, 83, 94].map((tx) => ({ x: px(tx) + 8, y: px(48) - 14, r: 60, color: warm })),
  ...[49, 61, 77, 93].map((tx) => ({ x: px(tx) + 8, y: px(45) - 14, r: 60, color: warm })),
  // The 85 Sky Tower lit up, the ferry pier and the lighthouse across the water.
  { x: px(87) + 8, y: px(38), r: 110, color: '140,200,255' }, { x: px(87) + 8, y: px(30), r: 80, color: '255,200,120' },
  { x: px(12), y: px(52), r: 60, color: warm }, { x: px(17) + 8, y: px(52), r: 50, color: '255,150,80' },
  { x: px(60), y: px(55), r: 60, color: '255,255,200' },
];

export const kaohsiung: MapDef = {
  id: 'kaohsiung',
  w: W,
  h: H,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#e6ebee', wallLight: '#f6f9fa', wainscot: '#3a6f9a', wallTop: '#26303a' },
  doors: [],
  decor,
  areas: [
    { key: 'kh_zuoying', rect: { x: 0, y: 0, w: px(48), h: px(16) } },
    { key: 'kh_lotus', rect: { x: px(48), y: 0, w: px(48), h: px(16) } },
    { key: 'kh_yancheng', rect: { x: 0, y: px(16), w: px(17), h: px(21) } },
    { key: 'kh_loveRiver', rect: { x: px(17), y: px(16), w: px(11), h: px(34) } },
    { key: 'kh_formosa', rect: { x: px(28), y: px(16), w: px(25), h: px(21) } },
    { key: 'kh_liuhe', rect: { x: px(53), y: px(16), w: px(43), h: px(21) } },
    { key: 'kh_pier2', rect: { x: 0, y: px(37), w: px(43), h: px(11) } },
    { key: 'kh_harbor', rect: { x: px(43), y: px(37), w: px(53), h: px(11) } },
    { key: 'kh_harbor', rect: { x: 0, y: px(48), w: px(W), h: px(8) } },
  ],
  lights,
  spots: {
    /** You step out of the HSR station onto the plaza. */
    train: { x: px(16), y: px(9) + 8, facing: 'down' },
    /** KMRT exits: Formosa Boulevard, Yanchengpu; LRT stops: Pier-2, Hamasen. */
    formosa: { x: px(42) + 8, y: px(23) + 8, facing: 'down' },
    yancheng: { x: px(32) + 8, y: px(33) + 8, facing: 'down' },
    pier2: { x: px(36) + 8, y: px(44) + 8, facing: 'down' },
    hamasen: { x: px(10) + 8, y: px(44) + 8, facing: 'down' },
  },
};
